import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { isRTL } from '@/core/i18n'
import {
  Plus,
  Search,
  Printer,
  Edit,
  Trash2,
  Copy,
  FileDown,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { showDeleteConfirm, showSuccess, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { useAuthStore } from '@/store/auth.store'
import { customersService } from '@/services/customersService'
import { Customer } from '@/api/types'

export const CustomersList: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const rtl = isRTL(i18n.language)
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [customers, setCustomers] = useState<Customer[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [deletingCustomerId, setDeletingCustomerId] = useState<number | null>(null)

  const branchId = user?.selectedBranch?.branchId

  // Load customers
  useEffect(() => {
    if (!branchId) {
      return
    }

    const loadCustomers = async () => {
      try {
        setLoading(true)
        const response = await customersService.getAllCustomers(branchId, pageNumber, pageSize)
        const list = response.data || []
        setCustomers(list)
        setTotalCount(response.totalRecords || list.length || 0)
        const pages = response.totalRecords ? Math.ceil(response.totalRecords / response.pageSize) : 0
        setTotalPages(pages || 1)
      } catch (error: any) {
        toast.error(error.response?.data?.message || t('customers.failedToLoadCustomers'))
      } finally {
        setLoading(false)
      }
    }

    loadCustomers()
  }, [branchId, pageNumber, pageSize])

  const filteredCustomers = useMemo(() => {
    if (!searchQuery.trim()) return customers
    const query = searchQuery.toLowerCase()
    return customers.filter((customer) => {
      return (
        (customer.customerNameEn || '').toLowerCase().includes(query) ||
        (customer.customerNameAr || '').toLowerCase().includes(query) ||
        (customer.email || '').toLowerCase().includes(query) ||
        (customer.mobileNo || customer.phone || '').toLowerCase().includes(query) ||
        String(customer.customerId || customer.id || '').includes(query)
      )
    })
  }, [customers, searchQuery])

  const handleAddNew = () => {
    navigate('/customers/new')
  }

  const handleEdit = (customer: Customer) => {
    const customerId = customer.customerId ?? customer.id
    if (!customerId) {
      toast.error(t('customers.invalidCustomerId'))
      return
    }
    navigate(`/customers/${customerId}/edit`)
  }

  const handleDelete = async (customer: Customer) => {
    const customerId = customer.customerId ?? customer.id
    if (!customerId || !branchId) {
      toast.error(t('customers.invalidCustomerId'))
      return
    }

    const customerName = customer.customerNameEn || customer.customerNameAr || t('customers.thisCustomer')
    // Format the confirmation message by replacing {name} with actual customer name
    const confirmMessageTemplate = t('customers.confirmDelete')
    const confirmMessage = confirmMessageTemplate.replace('{name}', customerName)
    
    const confirmed = await showDeleteConfirm(customerName, confirmMessage)
    if (!confirmed) {
      return
    }

    try {
      setDeletingCustomerId(customerId)
      await customersService.deleteCustomer(customerId, branchId)
      await showSuccess(
        t('customers.customerDeletedSuccess'),
        undefined,
        3000
      )
      // Reload customers
      const response = await customersService.getAllCustomers(branchId, pageNumber, pageSize)
      setCustomers(response.data || [])
      setTotalCount(response.totalRecords || 0)
    } catch (error: any) {
      await showError(
        t('customers.failedToDeleteCustomer'),
        error.response?.data?.message || t('customers.failedToDeleteCustomer'),
        5000
      )
    } finally {
      setDeletingCustomerId(null)
    }
  }

  const handlePrintList = () => {
    window.print()
  }

  const handleCopy = () => {
    const tableData = filteredCustomers.map(c => ({
      Id: c.customerId || c.id,
      Name: c.customerNameEn || '',
      'Name Arabic': c.customerNameAr || '',
      'Mobile No': c.mobileNo || '-',
      Email: c.email || '-',
      'Active Status': c.activeStatus ? 'Active' : 'Inactive',
    }))
    const csv = [
      Object.keys(tableData[0] || {}).join(','),
      ...tableData.map(row => Object.values(row).join(','))
    ].join('\n')
    navigator.clipboard.writeText(csv)
    toast.success(t('customers.copiedToClipboard'))
  }

  const handleExportCSV = () => {
    const tableData = filteredCustomers.map(c => ({
      Id: c.customerId || c.id,
      Name: c.customerNameEn || '',
      'Name Arabic': c.customerNameAr || '',
      'Mobile No': c.mobileNo || '-',
      Email: c.email || '-',
      'Active Status': c.activeStatus ? 'Active' : 'Inactive',
    }))
    const csv = [
      Object.keys(tableData[0] || {}).join(','),
      ...tableData.map(row => Object.values(row).join(','))
    ].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `customers_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success(t('customers.exportedToCsv'))
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={t('customers.title')}
        description={t('customers.manageCustomers')}
        action={{
          label: t('customers.addNew'),
          onClick: handleAddNew,
          icon: Plus,
        }}
      >
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handlePrintList}>
            <Printer className="h-4 w-4 mr-2" />
            {t('common.print')}
          </Button>
        </div>
      </PageHeader>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <label className="text-sm font-medium mb-2 block">{t('customers.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('customers.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button variant="outline" size="sm" onClick={handleCopy} className="flex-1 sm:flex-initial">
                <Copy className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">{t('customers.copy')}</span>
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportCSV} className="flex-1 sm:flex-initial">
                <FileDown className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">{t('customers.csv')}</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-muted-foreground">{t('customers.loadingCustomers')}</div>
            </div>
          ) : filteredCustomers.length === 0 ? (
            <EmptyState
              title={t('customers.noCustomersFound')}
              description={t('customers.getStarted')}
              action={{
                label: t('customers.addNewCustomerAction'),
                onClick: handleAddNew,
              }}
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className={`w-[80px] px-2 sm:px-4 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('customers.id')}</TableHead>
                      <TableHead className={`px-2 sm:px-4 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('customers.customerName')}</TableHead>
                      <TableHead className={`hidden md:table-cell px-2 sm:px-4 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('customers.nameArabic')}</TableHead>
                      <TableHead className={`px-2 sm:px-4 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('customers.mobileNo')}</TableHead>
                      <TableHead className={`hidden lg:table-cell px-2 sm:px-4 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('customers.email')}</TableHead>
                      <TableHead className={`px-2 sm:px-4 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('customers.status')}</TableHead>
                      <TableHead className="w-[100px] px-2 sm:px-4">{t('common.edit')}</TableHead>
                      <TableHead className="w-[100px] px-2 sm:px-4">{t('common.delete')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCustomers.map((customer) => {
                      const customerId = customer.customerId ?? customer.id
                      return (
                        <TableRow key={customerId}>
                          <TableCell className={`px-2 sm:px-4 ${rtl ? 'text-right' : 'text-left'}`}>{customerId}</TableCell>
                          <TableCell className={`font-medium px-2 sm:px-4 ${rtl ? 'text-right' : 'text-left'}`}>
                            {customer.customerNameEn || '-'}
                          </TableCell>
                          <TableCell className={`hidden md:table-cell px-2 sm:px-4 ${rtl ? 'text-right' : 'text-left'}`}>{customer.customerNameAr || '-'}</TableCell>
                          <TableCell className={`px-2 sm:px-4 ${rtl ? 'text-right' : 'text-left'}`}>{customer.mobileNo || customer.phone || '-'}</TableCell>
                          <TableCell className={`hidden lg:table-cell px-2 sm:px-4 ${rtl ? 'text-right' : 'text-left'}`}>{customer.email || '-'}</TableCell>
                          <TableCell className={`px-2 sm:px-4 ${rtl ? 'text-right' : 'text-left'}`}>
                            {customer.activeStatus ? (
                              <Badge variant="default">
                                <Check className="w-3 h-3 mr-1" />
                                {t('common.active')}
                              </Badge>
                            ) : (
                              <Badge variant="secondary">
                                <X className="w-3 h-3 mr-1" />
                                {t('common.inactive')}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="px-2 sm:px-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(customer)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </TableCell>
                          <TableCell className="px-2 sm:px-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(customer)}
                              disabled={deletingCustomerId === customerId}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 sm:px-6 py-4 border-t">
                  <div className="text-sm text-muted-foreground text-center sm:text-left">
                    {t('customers.showing')} {((pageNumber - 1) * pageSize) + 1} {t('customers.to')} {Math.min(pageNumber * pageSize, totalCount)} {t('customers.of')} {totalCount} {t('customers.customers')}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                      disabled={pageNumber === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <span className="hidden sm:inline">{t('customers.previous')}</span>
                    </Button>
                    <div className="text-sm whitespace-nowrap">
                      {t('customers.page')} {pageNumber} {t('customers.of')} {totalPages}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(p => Math.min(totalPages, p + 1))}
                      disabled={pageNumber === totalPages}
                    >
                      <span className="hidden sm:inline">{t('customers.next')}</span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
